(() => {
  const MAX_PAGES = 10;
  const REVIEW_CACHE_KEY = 'wls-okendo-review-pool-v1';
  const REVIEW_CACHE_TTL = 30 * 60 * 1000;

  const createElement = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
  };

  const getReviewImage = (review) =>
    review.media?.find((item) => item.type === 'image' && !item.isHidden && (item.largeUrl || item.fullSizeUrl || item.thumbnailUrl));

  const isEligibleReview = (review) =>
    Number(review.rating) > 4 && Boolean(review.title?.trim()) && Boolean(review.body?.trim()) && Boolean(getReviewImage(review));

  const createStars = (rating, section) => {
    const stars = createElement('div', 'wls-reviews__review-stars');
    const starTemplate = section.querySelector('[data-wls-review-star]');
    stars.setAttribute('aria-label', `Rated ${rating} out of 5 stars`);
    for (let index = 0; index < Math.round(rating); index += 1) {
      stars.append(starTemplate.content.cloneNode(true));
    }
    return stars;
  };

  const createReviewSlide = (review, section) => {
    const media = getReviewImage(review);
    const slide = createElement('div', 'wls-slider__slide swiper-slide');
    const container = createElement('div', 'wls-reviews__review-container');
    const inner = createElement('div', 'wls-reviews__review-inner');
    const imageWrap = createElement('div', 'wls-reviews__review-image');
    const image = createElement('img');
    const content = createElement('div', 'wls-reviews__review-content');
    const top = createElement('div', 'wls-reviews__review-top-content');
    const headline = createElement('h3', 'wls-reviews__review-headline', review.title.trim());
    const body = createElement('div', 'wls-reviews__review-text', review.body.trim());
    const bottomWrap = createElement('div', 'wls-reviews__review-bottom-content-wrapper');
    const separator = createElement('span', 'wls-reviews__review-content-separator');
    const bottom = createElement('div', 'wls-reviews__review-bottom-content');
    const reviewerName = createElement('span', 'wls-reviews__review-reviewer-name', review.reviewer?.displayName || 'Okendo Customer');

    image.src = media.largeUrl || media.fullSizeUrl || media.thumbnailUrl;
    image.alt = review.title.trim();
    image.loading = 'lazy';
    image.width = 500;
    image.height = 453;

    imageWrap.append(image);
    top.append(createStars(Number(review.rating), section), headline, body);
    bottom.append(reviewerName);

    if (review.reviewer?.isVerified) {
      const verified = createElement('div', 'wls-reviews__review-reviewer-verified');
      const label = createElement('span', 'wls-reviews__review-reviewer-verified-text', 'Verified Customer');
      const badgeTemplate = section.querySelector('[data-wls-review-verified-badge]');
      verified.append(badgeTemplate.content.cloneNode(true), label);
      bottom.append(verified);
    }

    bottomWrap.append(separator, bottom);
    content.append(top, bottomWrap);
    inner.append(imageWrap, content);
    container.append(inner);
    slide.append(container);
    return slide;
  };

  const getNextUrl = (data) => {
    const nextUrl = data.reviewsNextUrl || data.nextUrl;
    if (!nextUrl) return null;
    if (nextUrl.startsWith('http')) return nextUrl;
    return `https://api.okendo.io/v1${nextUrl.startsWith('/') ? '' : '/'}${nextUrl}`;
  };

  const shuffleReviews = (reviews) => {
    const shuffled = [...reviews];

    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const randomIndex = Math.floor(Math.random() * (index + 1));
      [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
    }

    return shuffled;
  };

  const orderReviews = (reviews, order, limit) => {
    const orderedReviews = order === 'newest' ? reviews : shuffleReviews(reviews);
    return orderedReviews.slice(0, limit);
  };

  const getCachedReviews = () => {
    try {
      const cached = JSON.parse(localStorage.getItem(REVIEW_CACHE_KEY));
      if (!cached?.reviews?.length || Date.now() - cached.timestamp > REVIEW_CACHE_TTL) {
        localStorage.removeItem(REVIEW_CACHE_KEY);
        return null;
      }
      return cached.reviews;
    } catch (error) {
      return null;
    }
  };

  const cacheReviews = (reviews) => {
    if (!reviews.length) return;
    try {
      localStorage.setItem(REVIEW_CACHE_KEY, JSON.stringify({ timestamp: Date.now(), reviews }));
    } catch (error) {
      // Storage may be disabled or full; fetching still works without caching.
    }
  };

  const updatePaginationVisibility = (section) => {
    const slideCount = section.querySelectorAll('.swiper-slide').length;
    section.classList.toggle('has-overflowing-pagination', slideCount > 20);
  };

  const prepareReviewText = (section) => {
    section.querySelectorAll('.wls-reviews__review-text').forEach((reviewText) => {
      const fullText = reviewText.dataset.fullReview || reviewText.innerText.trim();
      if (!fullText) return;

      reviewText.dataset.fullReview = fullText;
      const excerpt = createElement('span', 'wls-reviews__review-excerpt', fullText);
      reviewText.replaceChildren(excerpt);
      if (excerpt.scrollHeight <= excerpt.clientHeight + 1) return;

      const readMore = createElement('button', 'wls-reviews__read-more', 'Read more');
      readMore.type = 'button';
      readMore.setAttribute('aria-haspopup', 'dialog');
      reviewText.append(readMore);
    });
  };

  const initReviewDialog = (section) => {
    if (section.dataset.dialogInitialized === 'true') return;
    section.dataset.dialogInitialized = 'true';

    const dialog = section.querySelector('[data-wls-review-dialog]');
    const dialogText = section.querySelector('[data-wls-review-dialog-text]');
    const closeButton = section.querySelector('[data-wls-review-dialog-close]');
    if (!dialog || !dialogText || !closeButton) return;

    section.addEventListener('click', (event) => {
      const readMore = event.target.closest('.wls-reviews__read-more');
      if (!readMore) return;
      event.preventDefault();
      event.stopPropagation();
      dialogText.textContent = readMore.closest('.wls-reviews__review-text')?.dataset.fullReview || '';
      section.classList.add('is-dialog-open');
      // Native dialogs restore focus to the element that was active before
      // showModal(). If that element is inside a Swiper slide, Swiper moves to
      // that slide on close. Use the stable section container as focus return.
      section.setAttribute('tabindex', '-1');
      section.focus({ preventScroll: true });
      dialog.showModal();
    });

    closeButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      dialog.close();
    });
    dialog.addEventListener('close', () => section.classList.remove('is-dialog-open'));
    dialog.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    });
    dialog.addEventListener('pointerup', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    });
    dialog.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const bounds = dialog.getBoundingClientRect();
      const clickedOutside =
        event.clientX < bounds.left ||
        event.clientX > bounds.right ||
        event.clientY < bounds.top ||
        event.clientY > bounds.bottom;
      if (clickedOutside) {
        requestAnimationFrame(() => dialog.close());
      }
    });
  };

  const fetchReviews = async (endpoint, limit, order, onReady) => {
    const reviews = [];
    let url = endpoint;
    let page = 0;
    let hasRendered = false;

    while (url && page < MAX_PAGES) {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Okendo request failed with ${response.status}`);
      const data = await response.json();
      reviews.push(...(data.reviews || []).filter(isEligibleReview));
      url = getNextUrl(data);
      page += 1;

      if (!hasRendered && reviews.length >= limit) {
        hasRendered = true;
        onReady(orderReviews(reviews, order, limit));
      }
    }

    cacheReviews(reviews);
    return { reviews: orderReviews(reviews, order, limit), hasRendered };
  };

  const initSection = async (section) => {
    initReviewDialog(section);
    prepareReviewText(section);
    updatePaginationVisibility(section);

    if (section.dataset.source !== 'okendo' || section.dataset.okendoInitialized === 'true') return;
    section.dataset.okendoInitialized = 'true';

    const slider = section.querySelector('wls-slider');
    const wrapper = section.querySelector('.swiper-wrapper');
    const limit = Number(section.dataset.limit) || 8;
    const order = section.dataset.order || 'random';
    if (!slider || !wrapper) return;

    const renderReviews = (reviews) => {
      if (!reviews.length) return;
      section.classList.remove('is-loading');
      wrapper.replaceChildren(...reviews.map((review) => createReviewSlide(review, section)));
      updatePaginationVisibility(section);
      slider.setAttribute('aria-busy', 'false');
      slider.dispatchEvent(new CustomEvent('wls:slider:reinit'));
      requestAnimationFrame(() => prepareReviewText(section));
    };

    try {
      const cachedReviews = getCachedReviews();
      if (cachedReviews) {
        renderReviews(orderReviews(cachedReviews, order, limit));
        return;
      }

      const result = await fetchReviews(section.dataset.endpoint, limit, order, renderReviews);
      if (!result.reviews.length) throw new Error('No eligible Okendo reviews found');
      if (!result.hasRendered) renderReviews(result.reviews);
    } catch (error) {
      section.classList.remove('is-loading');
      slider.setAttribute('aria-busy', 'false');
      slider.dispatchEvent(new CustomEvent('wls:slider:reinit'));
      requestAnimationFrame(() => prepareReviewText(section));
      console.warn('[WLS Reviews] Using manual review fallback.', error);
    }
  };

  const initAll = (root = document) => root.querySelectorAll('[data-wls-reviews]').forEach(initSection);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  document.fonts?.ready.then(() =>
    document.querySelectorAll('[data-wls-reviews]').forEach(prepareReviewText)
  );

  let resizeFrame;
  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(() =>
      document.querySelectorAll('[data-wls-reviews]').forEach(prepareReviewText)
    );
  });
})();
